import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, call } from '../client';
import { qk } from '../keys';
import type { ProfileResponse, SettingsUpdate } from '../schema';

/** The signed-in account: profile, resolved entitlements, outstanding legal documents. */
export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => call(api.GET('/api/v1/users/me')),
    staleTime: 60_000,
  });
}

/** The profile, asserted present: only for components rendered inside the signed-in shell. */
export function useProfile(): ProfileResponse | undefined {
  return useMe().data;
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SettingsUpdate) => call(api.PATCH('/api/v1/users/me', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

/** This week's testing credits. Pass a quote to price a run through the charge path. */
export function useBalance(quote?: {
  strategy_id?: string;
  instrument_id?: string;
  instrument_ids?: string[];
  window_days?: number;
}) {
  return useQuery({
    queryKey: qk.balance(quote),
    queryFn: () => call(api.GET('/api/v1/testing/balance', { params: { query: quote ?? {} } })),
    staleTime: 15_000,
  });
}

export function useInstruments() {
  return useQuery({
    queryKey: qk.instruments,
    queryFn: () => call(api.GET('/api/v1/instruments')),
    staleTime: 10 * 60_000,
  });
}

/** The indicator registry: types, operators and parameter schemas that drive the builder form. */
export function useIndicators() {
  return useQuery({
    queryKey: qk.indicators,
    queryFn: () => call(api.GET('/api/v1/indicators')),
    staleTime: 30 * 60_000,
  });
}

export function useWeightPresets() {
  return useQuery({
    queryKey: qk.weightPresets,
    queryFn: () => call(api.GET('/api/v1/weight-presets')),
    staleTime: 30 * 60_000,
  });
}

/** The unread count for the rail badge. */
export function useUnreadAlerts() {
  return useQuery({
    queryKey: [...qk.alerts.all, 'unread-count'],
    queryFn: () => call(api.GET('/api/v1/alerts', { params: { query: { limit: 1 } } })),
    select: (d) => d.unread_count,
    refetchInterval: 60_000,
  });
}

/** Instruments by id, for resolving names in tables. */
export function useInstrumentMap() {
  const q = useInstruments();
  const map = new Map((q.data ?? []).map((i) => [i.id, i]));
  return { ...q, map };
}
